/** Default mandate validity when vendor does not return expiry_at (PhonePe, etc.) */
export const DEFAULT_MANDATE_VALIDITY_YEARS = 40;

export function parseApiDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === "string") {
    const dateOnly = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnly) {
      const d = new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3])
      );
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function addYears(date, years) {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

/** YYYY-MM-DD in local timezone (avoids UTC shift from toISOString). */
export function formatLocalDateString(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function normalizeDateString(value) {
  if (!value) return "";
  if (value instanceof Date) return formatLocalDateString(value);
  if (typeof value === "string") return value.slice(0, 10);
  return String(value).slice(0, 10);
}

export function resolveValidFrom(validFrom, fallbackDate) {
  const parsed = parseApiDate(validFrom);
  if (parsed) return parsed;
  if (fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())) {
    return fallbackDate;
  }
  return null;
}

/**
 * Use API valid_to when present; otherwise valid_from + 40 years (PhonePe / missing expiry).
 */
export function resolveValidTo(validFrom, validTo, years = DEFAULT_MANDATE_VALIDITY_YEARS) {
  const apiExpiry = parseApiDate(validTo);
  if (apiExpiry) return apiExpiry;
  const fromDate = resolveValidFrom(validFrom);
  if (!fromDate) return null;
  return addYears(fromDate, years);
}

export function fmtDisplayDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function defaultValidToFrom(validFrom) {
  const fromDate = resolveValidFrom(validFrom) || new Date();
  return formatLocalDateString(addYears(fromDate, DEFAULT_MANDATE_VALIDITY_YEARS));
}

export function mandateFieldsFromResponse(resp) {
  if (!resp) return {};
  const validFrom = normalizeDateString(resp.valid_from || "");
  const validTo = resp.valid_to
    ? normalizeDateString(resp.valid_to)
    : defaultValidToFrom(validFrom);
  return {
    partnerName: resp.partner_name || resp.paymentVendorName || "",
    mandateStatus: resp.mandate_status || "",
    validFrom,
    validTo,
    maxBillingAmount: resp.max_billing_amount != null ? resp.max_billing_amount : undefined,
  };
}

export function mandateFieldsFromVpaDetail(v) {
  if (!v) return {};
  const validFrom = normalizeDateString(
    v.valid_from || v.approved_at || v.added_at || ""
  );
  const validTo = v.valid_to || v.expiry_at
    ? normalizeDateString(v.valid_to || v.expiry_at)
    : defaultValidToFrom(validFrom);
  return {
    partnerName: v.partner_name || "",
    paymentVendorName: v.partner_name || "",
    mandateStatus: v.mandate_status || v.status_type_description || "",
    validFrom,
    validTo,
    maxBillingAmount: v.max_billing_amount != null ? v.max_billing_amount : undefined,
  };
}

/** Easebuzz returns a ready-made QR PNG URL — show as <img>, do not re-encode. */
export function isEasebuzzQrImage(paymentVendorCode, redirectUrl, redirectType) {
  if ((paymentVendorCode || "").toUpperCase() !== "EB") return false;
  if (!redirectUrl) return false;
  if (/^data:image\//i.test(redirectUrl)) return true;
  if (!/^https?:\/\//i.test(redirectUrl)) return false;
  if (/\.(png|jpe?g|gif|webp)(\?|$)/i.test(redirectUrl)) return true;
  if (/mandate_qr/i.test(redirectUrl)) return true;
  const type = (redirectType || "").toLowerCase();
  return type === "image" || type === "qr_image" || type === "url";
}

/** PhonePe, Razorpay, etc. — encode redirect_url (upi:// or https://) into a scannable QR. */
export function shouldRenderQrCode(paymentVendorCode, redirectUrl, redirectType) {
  if (!redirectUrl) return false;
  return !isEasebuzzQrImage(paymentVendorCode, redirectUrl, redirectType);
}

/** qr_image = Easebuzz PNG only; qr_code = all other vendors. */
export function getMandateAuthUiMode(paymentVendorCode, redirectUrl, redirectType) {
  if (isEasebuzzQrImage(paymentVendorCode, redirectUrl, redirectType)) return "qr_image";
  if (redirectUrl) return "qr_code";
  return "qr_code";
}

export function isMandatePollingComplete(resp, paymentVendorCode) {
  const txState = resp && resp.data && resp.data.transactionDetails && resp.data.transactionDetails.state;
  if (txState === "COMPLETED") return true;
  if (paymentVendorCode === "RP") {
    if (resp && resp.autopay_status_type_flag === "R") return true;
    if (resp && resp.success === true && resp.mandate_status) return true;
    const statusLabel = (resp && resp.mandate_status || "").toLowerCase();
    if (statusLabel === "registered" || statusLabel === "active") return true;
  }
  return false;
}

function paymentVendorDetailFromVpa(v) {
  if (!v) return null;
  const mapId = v.map_id != null ? v.map_id : v.partner_vendor_payment_account_id;
  const code = v.payment_vendor_code || "";
  if (mapId == null && !code) return null;
  return {
    map_id: mapId,
    payment_vendor_code: code,
    payment_vendor_name: v.partner_name || v.payment_vendor_name || code,
  };
}

function collectPaymentVendorsFromMembers(searchedMembers) {
  const byMapId = {};
  (searchedMembers || []).forEach((member) => {
    (member.vpaDetails || []).forEach((v) => {
      const detail = paymentVendorDetailFromVpa(v);
      if (!detail || detail.map_id == null) return;
      byMapId[String(detail.map_id)] = detail;
    });
  });
  return Object.values(byMapId);
}

export function resolvePaymentVendorDetails(apiDetails, searchedMembers) {
  const fromApi = apiDetails || [];
  if (fromApi.length > 0) return fromApi;
  return collectPaymentVendorsFromMembers(searchedMembers);
}

export function findRegisteredVpaMandate(vpaDetails) {
  if (!Array.isArray(vpaDetails)) return null;
  return vpaDetails.find((v) => v.autopay_status_type_flag === "R") || null;
}

export function findInitiatedVpaMandate(vpaDetails) {
  if (!Array.isArray(vpaDetails)) return null;
  return vpaDetails.find(
    (v) => v.autopay_status_type_flag === "I" && v.redirect_url
  ) || null;
}

export function findInitiatedVpaMandateForVendor(vpaDetails, mapId, paymentVendorCode) {
  if (!Array.isArray(vpaDetails)) return null;
  const initiated = vpaDetails.filter(
    (v) => v.autopay_status_type_flag === "I" && v.redirect_url
  );
  if (!initiated.length) return null;
  if (mapId != null) {
    const match = initiated.find(
      (v) => String(v.map_id != null ? v.map_id : v.partner_vendor_payment_account_id) === String(mapId)
    );
    if (match) return match;
  }
  const code = (paymentVendorCode || "").toUpperCase();
  if (code) {
    const match = initiated.find(
      (v) => (v.payment_vendor_code || "").toUpperCase() === code
    );
    if (match) return match;
  }
  return null;
}

export function providerFieldsFromVpaDetail(v) {
  if (!v) return {};
  return {
    payment_vendor_code: v.payment_vendor_code || "PP",
    mapId: v.map_id != null ? v.map_id : null,
    paymentVendorName: v.partner_name || "",
    partnerName: v.partner_name || "",
    provider: v.partner_name || "",
  };
}

export function contextFromRegisteredVpaMandate(v) {
  if (!v) return {};
  return {
    status: "success",
    authRequestId: v.auth_request_id || "",
    mandateRef:
      v.subscription_id ||
      v.third_reference_number_nach ||
      v.auth_request_id ||
      "",
    merchantUserId:
      v.merchant_user_id ||
      v.second_reference_number_nach ||
      "",
    ...providerFieldsFromVpaDetail(v),
    ...mandateFieldsFromVpaDetail(v),
  };
}

export function contextFromInitiatedVpaMandate(v) {
  if (!v) return {};
  return {
    redirectUrl: v.redirect_url || "",
    redirectType: v.redirect_type || "",
    merchantUserId:
      v.merchant_user_id ||
      v.second_reference_number_nach ||
      "",
    authRequestId: v.auth_request_id || "",
    mandateRef:
      v.subscription_id ||
      v.third_reference_number_nach ||
      v.auth_request_id ||
      "",
    ...providerFieldsFromVpaDetail(v),
    ...mandateFieldsFromVpaDetail(v),
  };
}
